
import React from 'react';
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { User, Bot, Wand2 } from 'lucide-react';
import { cn } from "@/lib/utils";

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  session_id: string;
  timestamp: string;
  isEnhanced?: boolean;
}

interface ChatMessageProps {
  message: ChatMessage;
}

const ChatMessage: React.FC<ChatMessageProps> = ({ message }) => {
  const isUser = message.role === 'user';
  const isSystem = message.role === 'system';
  const isEnhanced = message.isEnhanced;

  // Enhanced rich text formatting for AI responses
  const formatContent = (content: string) => {
    if (message.role === 'assistant') {
      return content
        .split('\n')
        .map((line, index) => {
          // Skip empty lines
          if (line.trim() === '') {
            return <br key={index} />;
          }

          // Headers (H1-H6)
          if (line.startsWith('# ')) {
            return <h1 key={index} className="text-2xl font-bold mb-3 text-gray-900 dark:text-white border-b pb-2">{line.slice(2)}</h1>;
          }
          if (line.startsWith('## ')) {
            return <h2 key={index} className="text-xl font-semibold mb-2 text-gray-800 dark:text-gray-200">{line.slice(3)}</h2>;
          }
          if (line.startsWith('### ')) {
            return <h3 key={index} className="text-lg font-medium mb-2 text-gray-700 dark:text-gray-300">{line.slice(4)}</h3>;
          }
          if (line.startsWith('#### ')) {
            return <h4 key={index} className="text-base font-medium mb-1 text-gray-700 dark:text-gray-300">{line.slice(5)}</h4>;
          }

          // Ordered lists
          if (line.match(/^\d+\.\s/)) {
            return (
              <div key={index} className="ml-6 mb-1">
                <span className="font-medium text-blue-600 dark:text-blue-400">{line.match(/^\d+\./)?.[0]}</span>
                <span className="ml-2">{line.replace(/^\d+\.\s/, '')}</span>
              </div>
            );
          }

          // Unordered lists
          if (line.startsWith('- ') || line.startsWith('* ')) {
            return (
              <div key={index} className="ml-4 mb-1 flex items-start">
                <span className="text-blue-600 dark:text-blue-400 mr-2 mt-1.5 w-1 h-1 bg-current rounded-full flex-shrink-0"></span>
                <span>{line.slice(2)}</span>
              </div>
            );
          }

          // Code blocks (simple detection)
          if (line.startsWith('```')) {
            return <div key={index} className="bg-gray-100 dark:bg-gray-800 p-3 rounded-md font-mono text-sm my-2">{line}</div>;
          }

          // Bold and italic text processing
          let processedLine = line;
          
          // Bold text (**text** or __text__)
          processedLine = processedLine.replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-gray-900 dark:text-white">$1</strong>');
          processedLine = processedLine.replace(/__(.*?)__/g, '<strong class="font-semibold text-gray-900 dark:text-white">$1</strong>');
          
          // Italic text (*text* or _text_)
          processedLine = processedLine.replace(/\*(.*?)\*/g, '<em class="italic">$1</em>');
          processedLine = processedLine.replace(/_(.*?)_/g, '<em class="italic">$1</em>');

          // Inline code (`code`)
          processedLine = processedLine.replace(/`([^`]+)`/g, '<code class="bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded text-sm font-mono">$1</code>');

          // Links [text](url)
          processedLine = processedLine.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-blue-600 dark:text-blue-400 hover:underline" target="_blank" rel="noopener noreferrer">$1</a>');

          return (
            <p 
              key={index} 
              className="mb-2 leading-relaxed" 
              dangerouslySetInnerHTML={{ __html: processedLine }} 
            />
          );
        });
    }
    
    // Regular content formatting for user and system messages
    return content.split('\n').map((line, index) => (
      <p key={index} className={line.trim() === '' ? 'mb-2' : 'mb-1'}>
        {line || '\u00A0'}
      </p>
    ));
  };

  if (isSystem) {
    return (
      <div className="flex justify-center my-4">
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-2">
          <p className="text-sm text-blue-700 dark:text-blue-300">{message.content}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn(
      "flex gap-3 w-full",
      isUser ? "justify-end" : "justify-start"
    )}>
      {!isUser && (
        <Avatar className="h-8 w-8 mt-1 flex-shrink-0">
          <AvatarFallback className={cn(
            "text-white text-xs",
            isEnhanced ? "bg-green-600" : "bg-blue-600"
          )}>
            {isEnhanced ? <Wand2 className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
          </AvatarFallback>
        </Avatar>
      )}
      
      <div className={cn(
        "max-w-[85%] rounded-lg px-4 py-3",
        isUser 
          ? "bg-primary text-primary-foreground ml-auto" 
          : isEnhanced
          ? "bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800"
          : "bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700"
      )}>
        {!isUser && (
          <div className="flex items-center gap-2 mb-2">
            <span className={cn(
              "text-xs font-medium",
              isEnhanced ? "text-green-700 dark:text-green-300" : "text-gray-700 dark:text-gray-300"
            )}>
              {isEnhanced ? "Enhanced Prompt" : "Product Copilot"}
            </span>
            {isEnhanced && (
              <div className="bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-300 px-2 py-0.5 rounded text-xs font-medium">
                AI Enhanced
              </div>
            )}
          </div>
        )}
        
        <div className={cn(
          "text-sm leading-relaxed",
          isUser 
            ? "text-primary-foreground" 
            : isEnhanced 
            ? "text-green-900 dark:text-green-100"
            : "text-gray-900 dark:text-gray-100"
        )}>
          {formatContent(message.content)}
        </div>
        
        <div className={cn(
          "text-xs mt-2 opacity-70",
          isUser ? "text-right" : "text-left"
        )}>
          {new Date(message.timestamp).toLocaleTimeString()}
        </div>
      </div>
      
      {isUser && (
        <Avatar className="h-8 w-8 mt-1 flex-shrink-0">
          <AvatarFallback className="bg-primary text-primary-foreground text-xs">
            <User className="h-4 w-4" />
          </AvatarFallback>
        </Avatar>
      )}
    </div>
  );
};

export default ChatMessage;
